/**
 * Point and vector helpers.
 *
 * Everything here is in canvas space (y grows downward) and is pure — no DOM,
 * no shared state — so the geometry engine can be unit tested without a
 * browser. ARCHITECTURE.md requires the geometry engine be kept separate from
 * rendering for exactly this reason.
 *
 * Ported from reference/bluetooth-room-sketch-prototype/index.html.
 */
import { degToRad } from './angles.js';

export function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Projects `rawEnd` onto the ray leaving `start` at `snappedAngle`, keeping the
 * ORIGINAL distance.
 *
 * This is the heart of snapping: the designer's drawn length is respected
 * exactly, and only the direction is corrected. Recomputing length from the
 * projection instead would shorten every wall by the cosine of the snap error,
 * silently shrinking the room.
 */
export function projectPoint(start, rawEnd, snappedAngle) {
  const length = distance(start, rawEnd);
  return {
    x: start.x + Math.cos(degToRad(snappedAngle)) * length,
    y: start.y + Math.sin(degToRad(snappedAngle)) * length,
  };
}

/** Advances `length` from `start` along an internal angle. */
export function advance(start, internalAngle, length) {
  return {
    x: start.x + Math.cos(degToRad(internalAngle)) * length,
    y: start.y + Math.sin(degToRad(internalAngle)) * length,
  };
}

/** Median of the positive, finite values in `numbers`; null when there are none. */
export function median(numbers) {
  const sorted = numbers.filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}
